import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { handleCallback, type CallbackResult } from '@/lib/oauth';
import { refreshSession } from '@/lib/session';

export const Route = createFileRoute('/auth/callback')({
  component: AuthCallbackPage,
});

/**
 * OAuth callback landing page.
 *
 * The auth server redirects here with ?code=...&state=... — we hand both
 * over to /lib/oauth handleCallback which:
 *   1. Validates state against what we stored before redirect
 *   2. POSTs code + code_verifier to backend /api/playground/auth/exchange
 *   3. Backend swaps code for tokens via /oauth/token, sets HttpOnly cookie
 *
 * On success → redirect to the page user was on before login.
 * On failure → show an actionable error with a retry CTA.
 */
function AuthCallbackPage() {
  const navigate = useNavigate();
  const [result, setResult] = useState<CallbackResult | 'pending'>('pending');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    handleCallback(params).then(async (r) => {
      setResult(r);
      if (r.ok) {
        // CRITICAL: bo_session cookie udah ke-set, tapi useSession() store
        // di TopBar/UserMenu masih cached "not authenticated" dari fetch
        // pertama (sebelum login). Tanpa refreshSession(), navigate akan
        // bawa user balik tapi avatar masih hidden. User akhirnya harus
        // klik Login lagi sebagai workaround.
        await refreshSession();

        // Slight delay so user sees success feedback briefly
        setTimeout(() => navigate({ to: r.returnTo || '/', replace: true }), 400);
      }
    });
  }, [navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md rounded-lg border border-border bg-elevated p-8 text-center">
        {result === 'pending' && <PendingState />}
        {result !== 'pending' && result.ok && <SuccessState />}
        {result !== 'pending' && !result.ok && (
          <ErrorState
            error={result.error || 'unknown_error'}
            description={result.errorDescription}
            returnTo={result.returnTo}
          />
        )}
      </div>
    </div>
  );
}

function PendingState() {
  return (
    <>
      <Loader2 size={32} className="mx-auto text-accent animate-spin mb-4" />
      <h1 className="text-lg font-semibold">Finishing sign-in…</h1>
      <p className="text-sm text-fg-tertiary mt-2">
        Exchanging your authorization code for a session.
      </p>
    </>
  );
}

function SuccessState() {
  return (
    <>
      <CheckCircle2 size={32} className="mx-auto text-success mb-4" />
      <h1 className="text-lg font-semibold">Signed in</h1>
      <p className="text-sm text-fg-tertiary mt-2">
        Redirecting you back…
      </p>
    </>
  );
}

function ErrorState({
  error,
  description,
  returnTo,
}: {
  error: string;
  description?: string;
  returnTo: string;
}) {
  return (
    <>
      <AlertCircle size={32} className="mx-auto text-error mb-4" />
      <h1 className="text-lg font-semibold">Sign-in failed</h1>
      <p className="text-sm text-fg-tertiary mt-2">
        {description || friendlyErrorMessage(error)}
      </p>
      <p className="text-2xs text-fg-muted font-mono mt-3">{error}</p>
      <div className="flex gap-2 justify-center mt-6">
        <a
          href={returnTo}
          className="inline-flex items-center px-3 h-9 rounded-md border border-border text-sm hover:bg-overlay transition-colors"
        >
          Back
        </a>
        <a
          href="/"
          className="inline-flex items-center px-3 h-9 rounded-md bg-accent text-bg-base text-sm font-medium hover:bg-accent-hover transition-colors"
        >
          Try again
        </a>
      </div>
    </>
  );
}

function friendlyErrorMessage(error: string): string {
  switch (error) {
    case 'access_denied':
      return 'You denied the authorization request.';
    case 'state_mismatch':
      return 'Something went wrong with the security check. Try signing in again.';
    case 'invalid_state':
      return 'Your session expired during sign-in. Try signing in again.';
    case 'exchange_failed':
      return "Couldn't finish setting up your session.";
    case 'login_required':
      return 'Sign-in is required.';
    default:
      return 'An unexpected error happened during sign-in.';
  }
}
